import * as userStoreClient from './user-store-client';

/**
 * In-memory registry of debounced league-save operations.
 * Populated by schedulePickPutDebounced (called from schedule-picker-base).
 * Flushed by flushPendingPicks on page unload (called from site.ts).
 *
 * Kept in a separate module so site.ts can import flushPendingPicks without
 * pulling in the full schedule-picker-base dependency tree (KO, toastr, etc.).
 */

interface PendingEntry {
  timer: ReturnType<typeof setTimeout>;
  saveString: string;
  sportId: number;
}

const pendingPicks = new Map<string, PendingEntry>();

/**
 * Schedules a debounced PUT for a league's saveString.
 * Any existing pending PUT for the same guid is cancelled and replaced.
 * The PUT fires after 15 seconds of inactivity.
 */
export function schedulePickPutDebounced(sportId: number, guid: string, saveString: string): void {
  const existing = pendingPicks.get(guid);
  if (existing !== undefined) {
    clearTimeout(existing.timer);
  }
  pendingPicks.set(guid, {
    sportId,
    saveString,
    timer: setTimeout(() => {
      const entry = pendingPicks.get(guid);
      if (entry === undefined) {
        return;
      }
      pendingPicks.delete(guid);
      void userStoreClient.putLeague(entry.sportId, guid, entry.saveString).catch((err: unknown) => {
        console.error('[V1 picks] putLeague failed', err);
      });
    }, 15_000),
  });
}

/**
 * Immediately flushes all pending pick PUTs using fetch keepalive so that
 * in-flight saves complete even when the page is being unloaded.
 * Called from site.ts on visibilitychange/pagehide events.
 */
export function flushPendingPicks(): void {
  for (const [guid, entry] of pendingPicks) {
    clearTimeout(entry.timer);
    void userStoreClient
      .putLeague(entry.sportId, guid, entry.saveString, { keepalive: true })
      .catch(() => {
        // keepalive — ignore errors on unload
      });
  }
  pendingPicks.clear();
}
